import { Selection, EnterElement, ScaleTime } from "d3"
import { D3OneToOneRenderable } from "./D3OneToOneRenderable"
import { ReactCallbacks } from "../../Types/ReactCallbacks"
import { TimeSeriesPageManager } from "../../Data/TimeSeriesPageManager"
import { ModalityPage } from "../../Data/ModalityPage"
import { getRealtimeValue } from "../../Data/TimeSeriesData"
import { findEMRStream, formatEMRAge, getEarliestEMRTime, getEMRReadingAt } from "../../Data/EMRValue"
import type { ModalityPageManager } from "../../Data/ModalityPageManager"
import { MobergFontFamily } from "../../../../../../Moberg"
import { DeltaRegionJSON, LineTraceConfigJSON, RenderStrategy } from "../../Types/Trace"
import { SummaryValueConfig } from "../../Types/Graph"
import { DataSource } from "../../Types/DataSource"

// A value at a picked time does not change, so once read it is kept for when that time's data
// is unloaded (scrolled far off screen, a new page size): returns the value read now, or the one
// kept earlier for the same measurement, or undefined ("?") when it was never read.
export function keepPickedValue(kept: Map<string, number>, key: string, value: number | undefined): number | undefined {
    if (value !== undefined && !Number.isNaN(value)) {
        kept.set(key, value)
        return value
    }
    return kept.get(key) ?? value
}

type DataIdentity = { dataSource?: unknown, dataKey: string, compositeIndex?: number }

// What a kept value belongs to: the row and the exact data it reads (source, data key and
// composite part, for both operands of a delta region). CPPopt, CPPopt LLA and CPPopt ULA share
// one data key and differ only by composite part, so a row switched between them must not
// show the previous one's kept number.
export function pickedValueKey(trace: { id: string, first?: DataIdentity, second?: DataIdentity } & Partial<DataIdentity>): string {
    const identity = (data: Partial<DataIdentity>) => `${String(data.dataSource ?? "")}|${data.dataKey ?? ""}|${data.compositeIndex ?? ""}`
    return trace.first && trace.second
        ? `${trace.id}|${identity(trace.first)}|${identity(trace.second)}`
        : `${trace.id}|${identity(trace)}`
}

// The number to the measurement's decimals. A small negative value that rounds to zero reads "0",
// not "-0" (a waveform crossing zero, for example ECG or Resp).
export function formatValue(value: number, decimals: number): string {
    const text = value.toFixed(decimals)
    return Number(text) === 0 ? (0).toFixed(decimals) : text
}

export type D3SummaryValuesConfig = {
    traces?: (LineTraceConfigJSON | DeltaRegionJSON)[]
    values?: SummaryValueConfig[]
    rows: number
    columns: number
    height: number
    width: number
    x: number
    y: number
}

type CustomCanvasRenderingContext2d = CanvasRenderingContext2D & {
    textRendering: "optimizeSpeed" | "optimizeLegibility" | "optimizePrecision"
}

export class D3SummaryValues extends D3OneToOneRenderable<SVGGElement, SVGForeignObjectElement, D3SummaryValuesConfig> {
    private pageManager: TimeSeriesPageManager<ModalityPage>
    private viewScale: ScaleTime<any, any, any>
    private context: CustomCanvasRenderingContext2d | null = null
    private currentValues: Map<string, number | undefined> = new Map() // The current value of each trace, by ID
    private doseAges: Map<string, number> = new Map() // How long before the reading time each dose shown was given, by trace ID
    private visibleTraces: (LineTraceConfigJSON | DeltaRegionJSON)[] = []
    private visibleValues: SummaryValueConfig[] = []
    private colorSchemeObserver: MutationObserver
    private textSizeCache: Map<string, number> = new Map() // string value to font size to prevent expensive re-measureing to find the text size.
    private isDarkMode: boolean = document.documentElement.getAttribute("data-color-scheme") === "dark"

    constructor(root: SVGGElement, config: D3SummaryValuesConfig, className: string, reactCallbacks: ReactCallbacks<any>, pageManager: TimeSeriesPageManager<ModalityPage>, viewScale: ScaleTime<any, any, any>) {
        super(root, config, className, reactCallbacks)
        this.pageManager = pageManager
        this.viewScale = viewScale

        this.colorSchemeObserver = new MutationObserver((mutations) => {
            for (const mutation of mutations) {
                if (mutation.attributeName === "data-color-scheme") {
                    this.isDarkMode = document.documentElement.getAttribute("data-color-scheme") === "dark"
                    this.render()
                    break
                }
            }
        })
        this.colorSchemeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ["data-color-scheme"] })

        this.updateDerivedState()
        this.render()
    }

    public destroy() {
        this.colorSchemeObserver.disconnect()
    }

    protected enter(newElements: Selection<EnterElement, D3SummaryValuesConfig, any, any>): Selection<SVGForeignObjectElement, D3SummaryValuesConfig, any, any> {
        const foreignObject = newElements.append("foreignObject")
            .attr("class", this.className)
            .attr("width", this.config.width)
            .attr("height", this.config.height)
            .attr("transform", `translate(${this.config.x}, ${this.config.y})`)

        const canvas = foreignObject.append("xhtml:canvas") as Selection<HTMLCanvasElement, D3SummaryValuesConfig, any, any>

        canvas
            .attr("width", this.config.width)
            .attr("height", this.config.height)

        const canvasNode = canvas.node()

        if (canvasNode != null) {
            this.context = canvasNode.getContext("2d") as CustomCanvasRenderingContext2d ?? null
            this.context.textRendering = "optimizeSpeed"
        }

        this.drawCanvas()

        return foreignObject
    }

    protected update(updatedElements: Selection<SVGForeignObjectElement, D3SummaryValuesConfig, any, any>): Selection<SVGForeignObjectElement, D3SummaryValuesConfig, SVGGElement, any> {
        updatedElements
            .attr("width", this.config.width)
            .attr("height", this.config.height)
            .attr("transform", `translate(${this.config.x}, ${this.config.y})`)

        updatedElements
            .select("canvas")
            .attr("width", this.config.width)
            .attr("height", this.config.height)

        this.setCurrentValue()
        this.drawCanvas()

        return updatedElements
    }

    protected updateDerivedState(): void {
        this.visibleTraces = this.config.traces?.filter(trace => trace.summaryValueConfig && trace.summaryValueConfig.isVisible) ?? []
        this.visibleValues = this.config.values?.filter(config => config.isVisible) ?? []
        this.textSizeCache.clear()
    }

    // The values read at the picked time (see setCurrentValue).
    private pickedTime: number | undefined = undefined
    private pickedValues = new Map<string, number>()

    protected setCurrentValue() {
        // With the cursor line placed, every number is the value at its time; otherwise the
        // latest value on screen.
        const atTime = this.reactCallbacks.getCursorTime?.() ?? undefined
        if (atTime !== this.pickedTime) {
            this.pickedTime = atTime
            this.pickedValues.clear()
        }

        this.visibleTraces.forEach(trace => {
            const dataObjectId = this.reactCallbacks.dataSourceMap.get(DataSource.CURRENT_PATIENT) ?? Infinity
            let value: number | undefined

            if (trace.isEMRData && trace.renderStrategy !== RenderStrategy.DELTA) {
                this.currentValues.set(trace.id, this.readEMRValue(trace, atTime))
                return
            }

            if (trace.renderStrategy === RenderStrategy.DELTA) {
                const firstValue = getRealtimeValue(dataObjectId, trace.first.dataKey, this.pageManager, trace.first, this.viewScale, atTime)
                const secondValue = getRealtimeValue(dataObjectId, trace.second.dataKey, this.pageManager, trace.second, this.viewScale, atTime)
                value = firstValue !== undefined && secondValue !== undefined ? firstValue - secondValue : undefined
            } else {
                value = getRealtimeValue(dataObjectId, trace.dataKey, this.pageManager, trace, this.viewScale, atTime)
            }

            if (atTime !== undefined) {
                value = keepPickedValue(this.pickedValues, pickedValueKey(trace), value)
            }

            this.currentValues.set(trace.id, value)
        })

        this.visibleValues.forEach(config => {
            this.currentValues.set(config.name, config.value)
        })
    }

    // Medication and flowsheet values come from the patient's whole EMR series, not from pages:
    // their pages are filled outside the socket load, so the page lookup can read the wrong one.
    // They are never kept for a picked time, since this series does not unload.
    private readEMRValue(trace: LineTraceConfigJSON, atTime: number | undefined): number | undefined {
        this.doseAges.delete(trace.id)

        const emrData = (this.pageManager as unknown as Partial<ModalityPageManager>).getEMRData?.() ?? []
        const stream = findEMRStream(emrData, trace.dataKey)

        if (!stream) {
            return undefined
        }

        const [viewStart, viewEnd] = this.viewScale.domain().map(date => date.getTime())
        const time = atTime ?? viewEnd
        const renderStrategy = trace.renderStrategy as RenderStrategy
        const reading = getEMRReadingAt(stream, renderStrategy, time, getEarliestEMRTime(time, viewStart, viewEnd))

        if (reading && renderStrategy === RenderStrategy.INTERMITTENT_MEDICATION) {
            this.doseAges.set(trace.id, time - reading.time)
        }

        return reading?.value
    }

    private drawCanvas() {
        if (!this.context) {
            return
        }

        const cols = this.config.columns ?? 1
        const rows = this.config.rows ?? 1

        const cellWidth = this.config.width / cols
        const cellHeight = Math.min(this.config.width * (this.config.traces?.length ?? this.config.values?.length ?? 1) / cols, this.config.height) / rows

        this.context.clearRect(0, 0, this.config.width, this.config.height)

        this.visibleTraces.forEach((trace, i) => {
            const col = i % cols
            const row = Math.floor(i / cols)
            const x = col * cellWidth
            const y = row * cellHeight

            let color: string
            let units: string

            if (trace.renderStrategy === RenderStrategy.DELTA) {
                color = `rgb(${trace.color.r}, ${trace.color.g}, ${trace.color.b})`
                units = trace.first.units
            } else {
                color = trace.color
                units = trace.units

            }

            const doseAge = this.doseAges.get(trace.id)

            if (doseAge !== undefined) {
                units = `${units} (${formatEMRAge(doseAge)} ago)`.trim()
            }

            const value = this.currentValues.get(trace.id)
            const text = (value !== undefined && !Number.isNaN(value)) ? `${formatValue(value, trace.summaryValueConfig?.precision ?? 0)}${trace.summaryValueConfig?.isPercent ? "%" : ""}` : "?"
            this.drawCell(x, y, cellWidth, cellHeight, text, trace.name, units, color)
        })

        this.visibleValues.forEach((config, i) => {
            let index = this.visibleTraces.length + i
            const col = index % cols
            const row = Math.floor(index / cols)
            const x = col * cellWidth
            const y = row * cellHeight

            const value = this.currentValues.get(config.name)
            const text = (value !== undefined && !Number.isNaN(value)) ? `${formatValue(value, config.precision ?? 0)}${config?.isPercent ? "%" : ""}` : "?"
            this.drawCell(x, y, cellWidth, cellHeight, text, config.name, config.units, config.color)
        })
    }

    private getTextColor(traceColor: string): string {
        return this.isDarkMode ? "white" : traceColor
    }

    private drawCell(x: number, y: number, width: number, height: number, valueText: string, labelText: string, unitsText: string | undefined, color: string) {
        if (!this.context) {
            return
        }

        const textColor = this.getTextColor(color)

        // Dynamic font sizing
        const horizontalPadding = 10
        const verticalPadding = 2

        const valueFont = this.fitText(valueText, width - 2 * horizontalPadding, height * (unitsText ? 0.45 : 0.6))
        this.context.font = valueFont
        this.context.fillStyle = textColor
        this.context.textAlign = "left"
        this.context.textBaseline = "top"
        this.context.fillText(valueText, x + horizontalPadding, y + 2 * verticalPadding + height * 0.3)

        // Label text (smaller)
        const labelFont = this.fitText(labelText, width - 2 * horizontalPadding, height * 0.3)
        this.context.font = labelFont
        this.context.fillStyle = textColor
        this.context.textBaseline = "top"
        this.context.fillText(labelText, x + horizontalPadding, y + verticalPadding)

        if (unitsText) {
            const unitsFont = this.fitText(unitsText, width - 2 * horizontalPadding, height * 0.15)
            this.context.font = unitsFont
            this.context.fillStyle = textColor
            this.context.textBaseline = "top"
            this.context.fillText(unitsText ?? "", x + horizontalPadding, y + verticalPadding + height * 0.75)
        }
    }

    private fitText(text: string, maxWidth: number, maxHeight: number) {
        if (!this.context) {
            return `40px ${MobergFontFamily.REGULAR}`
        }

        // Since numbers all have the same character width, we can make cache lookups more efficient by making them generic
        // e.g. "7000" -> "DDDD", "-0.1" -> "-D.D" instead of storing every unique value.
        const textNormalized = text.replace(/\d/g, 'D') 
        const cacheKey = `${textNormalized}|${maxWidth}|${maxHeight}`
        const cachedSize = this.textSizeCache.get(cacheKey)

        if (cachedSize) {
            return `${cachedSize}px ${MobergFontFamily.REGULAR}`
        }

        let min = 6, max = 100
        let best = min

        while (min <= max) {
            const mid = Math.floor((min + max) / 2)
            this.context.font = `${mid}px ${MobergFontFamily.REGULAR}`
            const { width } = this.context.measureText(text)
            const actualHeight = mid

            if (width <= maxWidth && actualHeight <= maxHeight) {
                best = mid
                min = mid + 1
            } else {
                max = mid - 1
            }
        }

        // Prevent the cache from growing too large.
        if (this.textSizeCache.size >= 1000) {
            this.textSizeCache.clear()
        }

        this.textSizeCache.set(cacheKey, best)

        return `${best}px ${MobergFontFamily.REGULAR}`
    }
}
